import parsePhoneNumberFromString from 'libphonenumber-js'
import * as z from 'zod'

import {t} from '../../../../lib/utils/localization'
import regex from './regex'

export type ValidationName = 'REQUIRED' | 'WORK_EMAIL_ONLY' | 'EMAIL' | 'PHONE'

export type ValidationBody = {
  message: string
  schema: z.ZodType
}

/**
 * Generic fallback used when a Contentful validation entry has no `errorMessage` and no
 * per-validation default applies. Contentful is the source of truth for validation copy; this is
 * only a safety net.
 */
export const GENERIC_FALLBACK = t('Please enter a valid value.')

/**
 * Per-validation fallback copy used when a Contentful `formFieldValidation` entry has no
 * `errorMessage`.
 *
 * TEMPORARY stepping stone: Contentful is the intended source of truth for validation copy. These
 * localized defaults keep specific messages during the content migration so a field without a
 * populated `errorMessage` does not degrade to {@link GENERIC_FALLBACK}. Once every entry has a
 * localized `errorMessage`, delete this map (and its translation keys) and let `getValidation` fall
 * back to {@link GENERIC_FALLBACK}. See the deferred follow-up in the plan.
 */
export const DEFAULT_VALIDATION_MESSAGES: Record<ValidationName, string> = {
  REQUIRED: t('This field is required.'),
  WORK_EMAIL_ONLY: t('Please enter a valid work email address.'),
  EMAIL: t('Please enter a valid email address.'),
  PHONE: t('Please enter a valid phone number.'),
}

/**
 * Schema-only catalog keyed by validation name. The codebase owns the schema; the message comes from
 * Contentful (`errorMessage`), then {@link DEFAULT_VALIDATION_MESSAGES}, then {@link GENERIC_FALLBACK}.
 */
export const FormValidationSchemas: Record<ValidationName, z.ZodType> = {
  REQUIRED: z.string().trim().min(1),
  WORK_EMAIL_ONLY: z.email().refine(email => !regex.EMAIL_PERSONAL_DOMAIN_REGEX.test(email), {
    message: t('The email is from a personal email provider.'),
  }),
  EMAIL: z.email(),
  PHONE: z
    .string()
    .trim()
    .refine(value => {
      const phoneNumber = parsePhoneNumberFromString(value, {extract: false})
      return phoneNumber !== undefined && phoneNumber.isPossible()
    }),
}
